/**
 * SDN Reseller API Service
 *
 * APIs para Resellers gerenciarem suas zonas SDN e atribuírem
 * zonas dedicadas aos seus End Customers.
 *
 * Fluxo B2B2B:
 * 1. Cloud Provider atribui zonas ao Reseller
 * 2. Reseller define zona padrão para seus clientes
 * 3. Reseller pode atribuir zonas dedicadas a customers específicos
 */

import { apiClient } from './api'

// ========================================
// Types
// ========================================

export type ResellerZoneInfo = {
  id: string
  zone_id: string
  name: string
  zone_type: string
  description?: string
  cluster_name?: string
}

export type ResellerZoneAssignment = {
  assignment_id: string
  zone: ResellerZoneInfo
  is_default: boolean
  is_dedicated: boolean
  dedicated_to?: {
    customer_id: string
    customer_name: string
  } | null
  assigned_at: string
  notes?: string
}

export type ResellerZonesResponse = {
  zones: ResellerZoneAssignment[]
  total: number
  default_zone: ResellerZoneAssignment | null
  has_default: boolean
}

export type CustomerVPCInfo = {
  id: string
  name: string
  vnet_id: string
  cidr: string
  sdn_zone?: string
}

export type VPCDetail = {
  id: string
  name: string
  description?: string
  vnet_id: string
  cidr: string
  gateway: string
  dns_servers?: string[]
  vni: number
  sdn_zone?: string
  is_default: boolean
  enabled: boolean
  current_allocations: number
  max_allocations?: number
  isolation_level: 'shared' | 'dedicated'
  created_at?: string
}

export type CustomerVPCsResponse = {
  customer: {
    id: string
    name: string
    network_isolation_level: string
  }
  vpcs: VPCDetail[]
  total: number
}

export type VPCDetailResponse = {
  vpc: VPCDetail
  customer: {
    id: string
    name: string
  }
}

export type UpdateVPCResponse = {
  message: string
  updated_fields: string[]
  vpc: VPCDetail
}

export type DeleteVPCResponse = {
  message: string
  vpc_id: string
  vnet_id: string
}

export type CustomerZoneInfo = {
  customer: {
    id: string
    name: string
    network_isolation_level: string
  }
  zone_assignment: 'default' | 'dedicated'
  dedicated_zone: {
    id: string
    zone_id: string
    name: string
    zone_type: string
  } | null
  vpc_count: number
  vpcs: CustomerVPCInfo[]
}

export type CustomersWithZonesResponse = {
  customers: CustomerZoneInfo[]
  total: number
  using_dedicated: number
  using_default: number
}

export type AvailableZoneForCustomer = {
  zone: {
    id: string
    zone_id: string
    name: string
    zone_type: string
  }
  is_default: boolean
  customers_using: { id: string; name: string }[]
  customers_count: number
}

export type AvailableZonesForCustomersResponse = {
  zones: AvailableZoneForCustomer[]
  total: number
}

export type DefaultZoneResponse = {
  has_default: boolean
  default_zone: {
    assignment_id: string
    zone: ResellerZoneInfo
  } | null
  message?: string
}

export type AssignZoneResponse = {
  message: string
  customer: {
    id: string
    name: string
    network_isolation_level: string
  }
  zone: {
    id: string
    zone_id: string
    name: string
  }
}

export type SetDefaultResponse = {
  message: string
  zone: {
    id: string
    zone_id: string
    name: string
  }
}

// Migration Types
export type VNETMigrationStatus =
  | 'pending'
  | 'stopping_vms'
  | 'preparing'
  | 'migrating'
  | 'applying_sdn'
  | 'cleaning_up'
  | 'starting_vms'
  | 'completed'
  | 'failed'
  | 'rolled_back'
  | 'cancelled'

export type VNETMigration = {
  id: string
  organization_id: string
  cluster_id: string
  network_pool_id: string | null
  source_zone_id: string
  target_zone_id: string
  source_vnet_id: string
  target_vnet_id: string | null
  status: VNETMigrationStatus
  current_step: number
  total_steps: number
  progress_percentage: number
  total_vms: number
  processed_vms: number
  vm_states: Record<string, { was_running: boolean; node: string; mac?: string }> | null
  error_message: string | null
  error_step: string | null
  triggered_by: string | null
  celery_task_id: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  // Extra fields from API
  customer_name?: string
  current_step_label?: string
}

export type MigrationInfo = {
  migration_initiated: boolean
  migrations_count: number
  migrations: {
    migration_id: string
    vnet_id: string
    source_zone: string
    target_zone: string
  }[]
  message: string
}

export type AssignZoneWithMigrationResponse = {
  migration?: MigrationInfo
} & AssignZoneResponse

export type MigrationsListResponse = {
  migrations: VNETMigration[]
  total: number
}

export type CustomerMigrationsResponse = {
  customer: {
    id: string
    name: string
  }
  active_migrations: VNETMigration[]
  finished_migrations: VNETMigration[]
  total: number
}

export type CancelMigrationResponse = {
  message: string
  migration_id: string
  task_id: string
}

// ========================================
// API Functions
// ========================================

export const sdnResellerApi = {
  /**
   * Lista todas as zonas SDN atribuídas ao Reseller atual.
   * Retorna as zonas que o Cloud Provider atribuiu a este reseller.
   */
  async listMyZones(): Promise<ResellerZonesResponse> {
    return await apiClient.get('/api/v1/sdn/reseller/zones')
  },

  /**
   * Define uma zona como padrão para os clientes do Reseller.
   * A zona padrão é usada automaticamente para novos End Customers.
   */
  async setDefaultZone(assignmentId: string): Promise<SetDefaultResponse> {
    return await apiClient.put(`/api/v1/sdn/reseller/zones/${assignmentId}/default`)
  },

  /**
   * Obtém a zona SDN padrão do Reseller.
   */
  async getDefaultZone(): Promise<DefaultZoneResponse> {
    return await apiClient.get('/api/v1/sdn/reseller/default-zone')
  },

  /**
   * Lista End Customers do Reseller e suas configurações de zona.
   * Mostra quais clientes têm zonas dedicadas e quais usam a zona padrão.
   */
  async listCustomersWithZones(): Promise<CustomersWithZonesResponse> {
    return await apiClient.get('/api/v1/sdn/reseller/customers')
  },

  /**
   * Atribui uma zona dedicada a um End Customer.
   * O Reseller só pode atribuir zonas que foram atribuídas a ele pelo CP.
   * Se o cliente tiver VNETs em outra zona, uma migração será iniciada automaticamente.
   */
  async assignDedicatedZoneToCustomer(
    customerId: string,
    zoneMetadataId: string,
  ): Promise<AssignZoneWithMigrationResponse> {
    return await apiClient.post(
      `/api/v1/sdn/reseller/customers/${customerId}/dedicated-zone`,
      null,
      { params: { zone_metadata_id: zoneMetadataId } },
    )
  },

  /**
   * Remove a zona dedicada de um End Customer.
   * O customer passará a usar a zona padrão do reseller.
   */
  async removeDedicatedZoneFromCustomer(customerId: string): Promise<{
    message: string
    customer: { id: string; name: string; network_isolation_level: string }
    info: string
  }> {
    return await apiClient.delete(`/api/v1/sdn/reseller/customers/${customerId}/dedicated-zone`)
  },

  /**
   * Lista zonas disponíveis para atribuição a End Customers.
   * Retorna apenas as zonas atribuídas ao Reseller pelo Cloud Provider.
   */
  async listAvailableZonesForCustomers(): Promise<AvailableZonesForCustomersResponse> {
    return await apiClient.get('/api/v1/sdn/reseller/available-zones-for-customers')
  },

  // ========================================
  // Migration APIs
  // ========================================

  /**
   * Lista todas as migrações dos clientes do Reseller.
   */
  async listMigrations(
    customerId?: string,
    statusFilter?: VNETMigrationStatus,
  ): Promise<MigrationsListResponse> {
    const params: Record<string, string> = {}
    if (customerId) params.customer_id = customerId
    if (statusFilter) params.status_filter = statusFilter
    return await apiClient.get('/api/v1/sdn/reseller/migrations', { params })
  },

  /**
   * Obtém status detalhado de uma migração específica.
   */
  async getMigrationStatus(migrationId: string): Promise<VNETMigration> {
    return await apiClient.get(`/api/v1/sdn/reseller/migrations/${migrationId}`)
  },

  /**
   * Cancela uma migração em andamento e faz rollback.
   */
  async cancelMigration(migrationId: string): Promise<CancelMigrationResponse> {
    return await apiClient.post(`/api/v1/sdn/reseller/migrations/${migrationId}/cancel`)
  },

  /**
   * Lista migrações de um cliente específico.
   */
  async listCustomerMigrations(customerId: string): Promise<CustomerMigrationsResponse> {
    return await apiClient.get(`/api/v1/sdn/reseller/customers/${customerId}/migrations`)
  },

  // ========================================
  // Customer VPC Management APIs
  // ========================================

  /**
   * Lista todas as VPCs de um cliente específico.
   */
  async listCustomerVPCs(customerId: string): Promise<CustomerVPCsResponse> {
    return await apiClient.get(`/api/v1/sdn/reseller/customers/${customerId}/vpcs`)
  },

  /**
   * Obtém detalhes de uma VPC específica.
   */
  async getCustomerVPC(customerId: string, vpcId: string): Promise<VPCDetailResponse> {
    return await apiClient.get(`/api/v1/sdn/reseller/customers/${customerId}/vpcs/${vpcId}`)
  },

  /**
   * Atualiza informações de uma VPC.
   */
  async updateCustomerVPC(
    customerId: string,
    vpcId: string,
    data: { name?: string; description?: string; enabled?: boolean },
  ): Promise<UpdateVPCResponse> {
    return await apiClient.put(`/api/v1/sdn/reseller/customers/${customerId}/vpcs/${vpcId}`, data)
  },

  /**
   * Deleta uma VPC de um cliente.
   *
   * Com recursos conectados, `migrateToVpcId` é obrigatório: o backend migra a
   * REDE deles (endereço fixo realocado na subnet de destino, cloud-init
   * reescrito, DHCP do gateway reconfigurado) antes de apagar a VNET (#804).
   * Sem ele, a resposta é 409 com a lista do que está conectado.
   *
   * `confirmNoGateway` libera um destino sem Cloud Gateway, onde as VMs ficam
   * sem DHCP e sem internet (#727).
   *
   * O parâmetro anterior (`force`) mandava `?force=true`, que o backend nunca
   * leu — a exclusão com recursos conectados recusava do mesmo jeito.
   */
  async deleteCustomerVPC(
    customerId: string,
    vpcId: string,
    options?: { migrateToVpcId?: string; confirmNoGateway?: boolean },
  ): Promise<DeleteVPCResponse> {
    const params = new URLSearchParams()
    if (options?.migrateToVpcId) params.set('migrate_to_vpc_id', options.migrateToVpcId)
    if (options?.confirmNoGateway) params.set('confirm_no_gateway', 'true')
    const query = params.toString()
    return await apiClient.delete(
      `/api/v1/sdn/reseller/customers/${customerId}/vpcs/${vpcId}${query ? `?${query}` : ''}`,
    )
  },
}

// ========================================
// Helper Functions
// ========================================

export function getNetworkIsolationLabel(level: string): string {
  const labels: Record<string, string> = {
    shared: 'Compartilhado',
    dedicated: 'Dedicado',
  }
  return labels[level] || level
}

export function getNetworkIsolationColor(level: string): string {
  const colors: Record<string, string> = {
    shared: 'bg-blue-100 text-blue-700',
    dedicated: 'bg-purple-100 text-purple-700',
  }
  return colors[level] || 'bg-gray-100 text-gray-700'
}

export function getZoneAssignmentLabel(assignment: 'default' | 'dedicated'): string {
  const labels: Record<string, string> = {
    default: 'Zona Padrão',
    dedicated: 'Zona Dedicada',
  }
  return labels[assignment] || assignment
}

export function getZoneAssignmentColor(assignment: 'default' | 'dedicated'): string {
  const colors: Record<string, string> = {
    default: 'bg-gray-100 text-gray-700',
    dedicated: 'bg-purple-100 text-purple-700',
  }
  return colors[assignment] || 'bg-gray-100 text-gray-700'
}

// Migration Helper Functions
export function getMigrationStatusLabel(status: VNETMigrationStatus): string {
  const labels: Record<VNETMigrationStatus, string> = {
    pending: 'Aguardando',
    stopping_vms: 'Parando VMs',
    preparing: 'Preparando',
    migrating: 'Migrando',
    applying_sdn: 'Aplicando SDN',
    cleaning_up: 'Limpando',
    starting_vms: 'Iniciando VMs',
    completed: 'Concluído',
    failed: 'Falhou',
    rolled_back: 'Revertido',
    cancelled: 'Cancelado',
  }
  return labels[status] || status
}

export function getMigrationStatusColor(status: VNETMigrationStatus): string {
  const colors: Record<VNETMigrationStatus, string> = {
    pending: 'bg-gray-100 text-gray-700',
    stopping_vms: 'bg-yellow-100 text-yellow-700',
    preparing: 'bg-blue-100 text-blue-700',
    migrating: 'bg-blue-100 text-blue-700',
    applying_sdn: 'bg-blue-100 text-blue-700',
    cleaning_up: 'bg-blue-100 text-blue-700',
    starting_vms: 'bg-yellow-100 text-yellow-700',
    completed: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    rolled_back: 'bg-orange-100 text-orange-700',
    cancelled: 'bg-gray-100 text-gray-700',
  }
  return colors[status] || 'bg-gray-100 text-gray-700'
}

export function isMigrationActive(status: VNETMigrationStatus): boolean {
  return [
    'pending',
    'stopping_vms',
    'preparing',
    'migrating',
    'applying_sdn',
    'cleaning_up',
    'starting_vms',
  ].includes(status)
}

export function isMigrationFinished(status: VNETMigrationStatus): boolean {
  return ['completed', 'failed', 'rolled_back', 'cancelled'].includes(status)
}
